<template>
  <div class="flex min-h-screen flex-col bg-gray-50 px-4 py-8">
    <div class="mx-auto w-full max-w-sm">
      <!-- Branding -->
      <div class="mb-4 flex items-center justify-center gap-2">
        <img src="/assets/trellis-logo.svg" alt="Trellis" class="h-6 w-auto" />
        <span class="text-xs font-medium text-gray-400">ID Verification</span>
      </div>

      <!-- Step indicator -->
      <div class="mb-4 flex justify-center gap-2">
        <div class="h-2 w-2 rounded-full bg-green-600" />
        <div class="h-2 w-6 rounded-full bg-green-600" />
      </div>

      <!-- === UPLOADING STATE === -->
      <template v-if="uploading">
        <div class="mt-12 text-center">
          <h1 class="text-lg font-semibold text-gray-900">Uploading…</h1>

          <div class="mt-6 flex flex-col items-center gap-3">
            <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                class="h-full rounded-full bg-green-600 transition-all duration-300"
                :style="{ width: `${uploadProgress}%` }"
              />
            </div>
            <p class="text-sm text-gray-500">{{ uploadProgress }}%</p>
          </div>

          <div v-if="uploadError" class="mt-4">
            <p class="text-sm text-red-600">{{ uploadError }}</p>
            <button
              type="button"
              class="mt-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white"
              @click="submitPhoto"
            >
              Retry
            </button>
          </div>
        </div>
      </template>

      <!-- === INSTRUCTIONS + CAPTURE STATE === -->
      <template v-else>
        <div class="mt-8 text-center">
          <div
            class="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50"
          >
            <svg
              class="h-10 w-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z"
              />
            </svg>
          </div>

          <h1 class="text-xl font-semibold text-gray-900">
            Take a Photo of Yourself
          </h1>

          <p class="mt-3 text-sm leading-relaxed text-gray-500">
            Now we need a photo of you holding your ID next to your face for
            identity verification.
          </p>

          <div class="mt-6 space-y-2 text-left">
            <div class="flex items-start gap-3 rounded-lg bg-white p-3">
              <span class="mt-0.5 text-green-600">✓</span>
              <span class="text-sm text-gray-600"
                >Hold your ID next to your face</span
              >
            </div>
            <div class="flex items-start gap-3 rounded-lg bg-white p-3">
              <span class="mt-0.5 text-green-600">✓</span>
              <span class="text-sm text-gray-600"
                >Make sure your face and the ID are both clearly visible</span
              >
            </div>
            <div class="flex items-start gap-3 rounded-lg bg-white p-3">
              <span class="mt-0.5 text-green-600">✓</span>
              <span class="text-sm text-gray-600"
                >Find a well-lit area and remove sunglasses or hats</span
              >
            </div>
          </div>

          <!-- Preview of captured photo -->
          <div v-if="previewUrl" class="mt-6">
            <img
              :src="previewUrl"
              alt="Captured face photo"
              class="mx-auto rounded-xl"
            />
          </div>

          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            capture="user"
            class="hidden"
            @change="onFileSelected"
          />

          <button
            type="button"
            class="mt-8 w-full rounded-xl bg-green-600 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98]"
            @click="openCamera"
          >
            {{ previewUrl ? "Retake Photo" : "Take Photo" }}
          </button>

          <button
            v-if="capturedFile"
            type="button"
            class="mt-3 w-full rounded-xl bg-green-700 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98]"
            @click="submitPhoto"
          >
            Use This Photo
          </button>
        </div>
      </template>

      <!-- Participant name -->
      <p class="mt-4 text-center text-xs text-gray-400">
        {{ participantName }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from "vue"

const props = defineProps<{
  token: string
  participantName: string
  status: string
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const capturedFile = ref<File | null>(null)
const previewUrl = ref<string | null>(null)
const uploading = ref(false)
const uploadProgress = ref(0)
const uploadError = ref<string | null>(null)

function openCamera() {
  fileInput.value?.click()
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  capturedFile.value = file
  input.value = ""

  // User already confirmed in camera — submit immediately
  submitPhoto()
}

async function captureLocation(): Promise<{
  latitude: number
  longitude: number
}> {
  try {
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        })
      },
    )

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    }
  } catch {
    return { latitude: 0, longitude: 0 }
  }
}

async function submitPhoto() {
  if (!capturedFile.value) return

  uploading.value = true
  uploadProgress.value = 0
  uploadError.value = null

  try {
    const location = await captureLocation()

    const formData = new FormData()
    formData.append("photo", capturedFile.value)
    formData.append("latitude", location.latitude.toString())
    formData.append("longitude", location.longitude.toString())

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          uploadProgress.value = Math.round((e.loaded / e.total) * 100)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 400) {
          resolve()
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      })

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"))
      })

      xhr.open("POST", `/verification/${props.token}/face`)

      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )?.content
      if (csrfToken) {
        xhr.setRequestHeader("X-CSRF-Token", csrfToken)
      }

      xhr.send(formData)
    })

    window.location.href = `/verification/${props.token}/complete`
  } catch (err) {
    uploading.value = false
    uploadError.value =
      err instanceof Error ? err.message : "Upload failed. Please try again."
  }
}

onUnmounted(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
})

defineOptions({ layout: false })
</script>
